import React from "react";
import { Heart, Code2, Mail, Github, Linkedin, Instagram, Sparkles, Coffee } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'Email',
      icon: Mail,
      url: 'mailto:kinandaru@gmail.com',
      gradient: 'from-[#3A7CA5] to-[#2E6B94]'
    },
    {
      name: 'GitHub',
      icon: Github,
      url: 'https://github.com/kinanprastiwi',
      gradient: 'from-gray-700 to-black'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: 'https://www.linkedin.com/in/kinandaru-pramudya-prastiwi/',
      gradient: 'from-blue-600 to-blue-700'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://www.instagram.com/kinandaru_1/',
      gradient: 'from-pink-500 to-purple-500'
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-[#3D3D3D] dark:bg-gray-950 text-white transition-colors duration-300 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-[#3A7CA5]/20 rounded-full animate-pulse-slow"></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-[#E6B566]/20 rounded-full animate-pulse-slow animation-delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-[#A9D6E5]/20 rounded-full animate-pulse-slow animation-delay-2000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2 animate-slideInLeft">
            <div className="flex items-center gap-3 mb-4 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-[#3A7CA5] to-[#A9D6E5] rounded-lg flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                {/* Floating ring */}
                <div className="absolute inset-0 border border-[#E6B566]/30 rounded-lg animate-spin-slow"></div>
              </div>
              <h3 className="text-2xl font-bold group-hover:gradient-text transition-all duration-300">
                Kinandaru
              </h3>
              <Sparkles className="w-5 h-5 text-[#E6B566] animate-pulse" />
            </div>
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
Siswa SMK dengan minat pada pengembangan fullstack dan desain UI/UX. 
              Berkomitmen menciptakan solusi digital yang bermanfaat dan berdampak nyata.
            </p>
            <div className="flex items-center gap-2 text-gray-400 group">
              <span>Dibangun dengan</span>
              <Heart className="w-4 h-4 text-red-400 animate-pulse group-hover:scale-125 transition-transform duration-300" />
              <span>menggunakan React & Tailwind CSS</span>
              <Coffee className="w-4 h-4 text-[#E6B566] group-hover:rotate-12 transition-transform duration-300" />
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-slideInUp animation-delay-300">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              Link Penting
              <div className="w-2 h-2 bg-[#3A7CA5] rounded-full animate-pulse"></div>
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', section: 'hero' },
                { name: 'About', section: 'about' },
                { name: 'Projects', section: 'projects' },
                { name: 'Contact', section: 'contact' }
              ].map((link, index) => (
                <li key={link.name} className="animate-fadeInScale" style={{ animationDelay: `${index * 100}ms` }}>
                  <button 
                    onClick={() => scrollToSection(link.section)}
                    className="text-gray-400 hover:text-white hover:text-[#A9D6E5] transition-all duration-300 transform hover:translate-x-2 flex items-center gap-2 group"
                  >
                    <div className="w-1 h-1 bg-[#E6B566] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="animate-slideInUp animation-delay-500">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              Koneksi
              <div className="w-2 h-2 bg-[#E6B566] rounded-full animate-pulse"></div>
            </h4>
            <div className="space-y-3 mb-6">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-400 hover:text-white transition-all duration-300 transform hover:translate-x-2 group animate-fadeInScale"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative">
                      <IconComponent className="w-4 h-4 group-hover:rotate-12 transition-transform duration-200" />
                      <div className={`absolute inset-0 bg-gradient-to-r ${social.gradient} rounded-full opacity-0 group-hover:opacity-20 group-hover:scale-150 transition-all duration-300`}></div>
                    </div>
                    <span className="group-hover:text-[#A9D6E5] transition-colors duration-200">
                      {social.name}
                    </span>
                  </a>
                );
              })}
            </div>

            {/* Social Icons Grid */}
            <div className="flex gap-2">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={`icon-${social.name}`}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-all duration-300 transform hover:scale-125 hover:rotate-12 group animate-fadeInScale"
                    style={{ animationDelay: `${index * 50 + 600}ms` }}
                  >
                    <IconComponent className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors duration-200" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 mt-12 pt-8 animate-slideInUp animation-delay-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2 group">
                <span>© {currentYear} Kinandaru Pramudya Prastiwi</span>
                <Heart className="w-3 h-3 text-red-400 animate-pulse" />
              </div>
              <span className="hidden md:block">•</span>
              <span className="group-hover:text-[#A9D6E5] transition-colors duration-200">SMK Negeri 10 Jakarta</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-400 group">
              <Code2 className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
              <span className="group-hover:text-[#A9D6E5] transition-colors duration-200">
                Pengembang Fullstack & Desainer UI/UX
              </span>
              <Sparkles className="w-4 h-4 text-[#E6B566] animate-pulse" />
            </div>
          </div>

          {/* Fun fact */}
          <div className="text-center mt-6 text-xs text-gray-500 animate-fadeInScale animation-delay-1000">
            <p className="flex items-center justify-center gap-2 hover:text-gray-400 transition-colors duration-200 cursor-default group">
              <Coffee className="w-3 h-3 group-hover:rotate-12 transition-transform duration-200" />
             Ditenagai oleh semangat, kopi, dan rasa ingin tahu tanpa batas
              <Heart className="w-3 h-3 text-red-400 animate-pulse" />
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;